import { BarChart, Database, FileType, Layers } from 'lucide-react';
import { useMemo } from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useWorkfrontObjectCatalog } from '../hooks.js';

/**
 * Workfront · Reporting. Today derives reporting bars client-side
 * from the engine-detected catalog — counts of objects per Workfront
 * type. Live Workfront reports (recharts on the customer's actual
 * report data) land alongside the customer-OAuth-credentialed
 * Workfront API proxy in Stage 2 of the workfront slice.
 */
export function WorkfrontReportingPage(): JSX.Element {
  const { data: rows = [], isLoading, error } = useWorkfrontObjectCatalog();

  const counts = useMemo(() => countByType(rows), [rows]);
  const totals = useMemo(() => {
    const totalObjects = rows.length;
    const totalForms = rows.filter((r) => {
      const t = (r.workfrontTypeLabel ?? r.workfrontType ?? '') as string;
      return t.toLowerCase().includes('form') || t === 'CTGY';
    }).length;
    const totalScenarios = new Set<string>();
    for (const r of rows) {
      const links = r.fusionLinks;
      if (Array.isArray(links)) {
        for (const l of links as Array<{ fusionScenarioFusionId?: string }>) {
          if (l.fusionScenarioFusionId) totalScenarios.add(l.fusionScenarioFusionId);
        }
      }
    }
    return { totalObjects, totalForms, totalScenarios: totalScenarios.size };
  }, [rows]);

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Workfront · Reporting</h1>
        <p className="text-sm text-foreground/60">
          Live overview of the Workfront-object footprint across this license. Custom Workfront
          reports (run-on-demand) land with Stage 2 of the workfront slice.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 px-6 py-3 sm:grid-cols-3">
        <SummaryTile icon={Database} label="Objects detected" value={totals.totalObjects} />
        <SummaryTile icon={FileType} label="Custom forms" value={totals.totalForms} />
        <SummaryTile icon={Layers} label="Scenarios referencing" value={totals.totalScenarios} />
      </div>

      <div className="px-6 py-3">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            Could not load Workfront objects: {error.message}
          </div>
        ) : isLoading ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            Loading…
          </div>
        ) : counts.length === 0 ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            No Workfront objects detected yet.
          </div>
        ) : (
          <section className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-4">
            <header className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground/80">
              <BarChart className="h-4 w-4" />
              Objects by Workfront type
            </header>
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <RechartsBarChart
                  data={[...counts]}
                  margin={{ top: 8, right: 8, bottom: 8, left: 8 }}
                >
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                    stroke="rgba(255,255,255,0.2)"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }}
                    stroke="rgba(255,255,255,0.2)"
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255,255,255,0.06)' }}
                    contentStyle={{
                      backgroundColor: 'rgba(7, 23, 48, 0.95)',
                      border: '1px solid rgba(255,255,255,0.15)',
                      borderRadius: 6,
                      color: '#fff',
                    }}
                    labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                  />
                  <Bar dataKey="value" fill="hsl(var(--chart-1))" radius={[4, 4, 0, 0]} />
                </RechartsBarChart>
              </ResponsiveContainer>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function countByType(
  rows: ReadonlyArray<Record<string, unknown>>,
): ReadonlyArray<{ label: string; value: number }> {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const t =
      (typeof r.workfrontTypeLabel === 'string' && r.workfrontTypeLabel) ||
      (typeof r.workfrontType === 'string' && r.workfrontType) ||
      'Unknown';
    counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
}

function SummaryTile({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof BarChart;
  label: string;
  value: number;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-md border border-foreground/10 bg-foreground/[0.03] p-4">
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-cyan-500/15">
        <Icon className="h-5 w-5 text-cyan-700 dark:text-cyan-300" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-xs uppercase tracking-wide text-foreground/50">{label}</div>
        <div className="mt-0.5 text-2xl font-semibold text-foreground">{value}</div>
      </div>
    </div>
  );
}
